import { defaultSchema } from 'rehype-sanitize';

/** The sanitize schema shape (rehype-sanitize does not re-export the type). */
type Schema = typeof defaultSchema;

/**
 * rehype-sanitize schema extended to preserve the markup that KaTeX, Shiki, and
 * our Mermaid placeholder emit (class + inline style + MathML), while still
 * dropping `<script>`, event-handler attributes, and `javascript:` URLs that may
 * be authored as raw HTML in the markdown source.
 *
 * Security note: we deliberately keep the default GitHub schema's element/URL
 * filtering (scripts, iframes, event handlers are NOT added). We only widen what
 * is needed for trusted, library-generated output.
 */

// MathML elements emitted by KaTeX (output: 'htmlAndMathml') for accessibility.
const MATHML_TAGS = [
  'math',
  'semantics',
  'annotation',
  'annotation-xml',
  'mrow',
  'mi',
  'mo',
  'mn',
  'ms',
  'mtext',
  'mspace',
  'msup',
  'msub',
  'msubsup',
  'mfrac',
  'mroot',
  'msqrt',
  'munder',
  'mover',
  'munderover',
  'mmultiscripts',
  'mtable',
  'mtr',
  'mtd',
  'mlabeledtr',
  'mphantom',
  'mpadded',
  'mstyle',
  'mglyph',
  'menclose',
  'merror',
  'maction',
  'mprescripts',
  'none',
];

function buildSchema(): Schema {
  const base = structuredClone(defaultSchema);

  const attributes: NonNullable<Schema['attributes']> = base.attributes ?? {};
  const existingStar = attributes['*'] ?? [];

  // Allow the presentational attributes the libraries rely on, everywhere.
  attributes['*'] = [
    ...existingStar,
    'className',
    // Shiki emits raw HTML attribute names (`class`, `tabindex`) rather than the
    // hast property spellings, so allow both forms.
    'class',
    'style',
    'id',
    'tabIndex',
    'tabindex',
    'ariaHidden',
    'role',
    // MathML presentation attributes
    'mathVariant',
    'displayStyle',
    'scriptLevel',
    'display',
    'xmlns',
    'encoding',
    'stretchy',
    'fence',
    'separator',
    'accent',
    'accentUnder',
    'columnAlign',
    'rowAlign',
    'open',
    'close',
    'linethickness',
  ];

  // Keep heading/footnote ids as their clean slug (no `user-content-` prefix) so
  // in-document anchor links and the generated TOC resolve. Script/handler
  // sanitization is unaffected.
  base.clobberPrefix = '';

  const tagNames = new Set(base.tagNames ?? []);
  for (const tag of MATHML_TAGS) tagNames.add(tag);
  // span/div are needed by KaTeX and Shiki output.
  tagNames.add('span');
  tagNames.add('div');
  base.tagNames = [...tagNames];

  base.attributes = attributes;
  return base;
}

export const sanitizeSchema: Schema = buildSchema();
